// 로그인 페이지 (Step 8.3 + Phase B 트랙 B cs-tokens 변환 + 1.0.1 D-1 브랜드 히어로 개편)
// - 구조(2026-09-02 D-1): 상단 ~40% 브랜드 히어로(워드마크·가치 한 줄·황둥이) + 하단 로그인 영역
//   위계 = 소셜 4종 52×52 아이콘 버튼 한 줄(Apple 맨 앞) > 구분선 > 이메일 폼(기본 펼침 · 2026-09-04 효신님 결정)
//   소셜이 폼 위인 이유: HIG "Apple 버튼은 스크롤 없이 보이게" — 폼(≈170px)이 위면 SE3(667)에서 소셜이 fold 밖으로 밀린다
//   (2026-09-04 효신님 결정: Apple 풀폭 주 버튼 → 아이콘으로 통일 · Android 앱에서는 Apple 비노출)
// - Apple + Google + Naver + Kakao 4개 provider — OAuth 버튼 브랜드 색 유지 (효신님 결정 2026-05-26)
//   Apple은 App Store 4.8(Login Services) 필수라 2026-08-19 추가 — HIG "다른 로그인 버튼보다 작지 않게 + 스크롤 없이"가 요건
// - deep link 콜백 이벤트(`sotda:auth:login-success`/`-error`) 수신해서 UI 반영
// - 페이지 배경·텍스트·에러박스·링크 = cs-tokens var() (light/mocha 자동 대응). 히어로는 --cs-hero-ink 고정(모드 무관)
// design doc: 2026-05-26-finance-to-sotda-design-mapping §7 Phase B · 2026-09-02-sotda-login-redesign-design (D-1·D-3·D-5)

import { useCallback, useEffect, useRef, useState, type FormEvent, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { TabLayout } from "../layouts/TabLayout";
import { getWebOAuthRedirectUri, startAppleOAuth, startGoogleOAuth, startNaverOAuth, startKakaoOAuth } from "../lib/oauth";
import { supabase } from "../lib/supabase";
import { passwordStatus, validateNickname, validateSignupForm, translateAuthError } from "@core/auth/emailRules";
import { resolveNicknameCheck, isAuthBusy } from "../lib/authFlow";
import { isOnboarded } from "../lib/onboarding";
import { flushPendingMbtiDiagnosis } from "../lib/pendingMbtiDiagnosis";
import { openExternal, TERMS_URL, PRIVACY_URL, FORGOT_PASSWORD_URL } from "../lib/externalLink";
import "../redesign/cs-tokens.css";

type LoadingProvider = "apple" | "google" | "naver" | "kakao" | null;

// 가입/재설정 확인 메일 링크가 도착할 도메인 — 브랜딩 도메인 sotda.com 고정
// (Supabase uri_allow_list에 sotda.com/** 등록됨. API 호출 base(www.choishin.net 경유)와 분리:
//  메일 링크만 sotda.com으로 보내 옛 도메인 로그인 화면으로 새는 것 방지)
const EMAIL_REDIRECT_BASE = "https://sotda.com";

// Turnstile 사이트키 — 웹(app/auth/*)과 **같은 위젯**을 쓴다(키가 갈리면 호스트명 관리가 두 벌이 된다).
// 미설정이면 위젯을 렌더하지 않고 토큰 없이 진행한다(웹과 같은 fail-open) —
// Supabase에서 CAPTCHA를 켜기 전까지는 토큰이 없어도 인증이 통과하므로 이 상태가 정상이다.
//
// ⚠ 이 화면은 **앱과 웹 SPA(app.sotda.com)가 공유**한다 → Cloudflare 위젯 호스트명에
//    `localhost`(앱 · iosScheme https)와 `app.sotda.com`(SPA)이 **둘 다** 있어야 한다.
//    빠지면 그 출처에서만 위젯이 조용히 안 뜬다.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

// 🔴 네이티브 앱에서는 위젯을 아예 렌더하지 않는다 (2026-08-17) —
//   Turnstile 웹 위젯은 WKWebView를 공식 미지원이라 앱에서는 항상 "확인 실패"(600010)로
//   끝난다. allowNavigation 등 설정으로 풀리지 않음을 실기로 확정했다.
//   실패가 확정된 위젯을 리뷰어·사용자에게 보여줄 이유가 없다.
//   앱 캡차는 Turnstile Mobile SDK 브릿지(오픈 후 과제)로만 가능하다.
//   웹 SPA는 위젯이 정상 동작하므로 유지 — Supabase 서버 CAPTCHA 재활성 시 웹은 그대로 복원된다.
const CAPTCHA_WIDGET_ENABLED = !!TURNSTILE_SITE_KEY && !Capacitor.isNativePlatform();

// Apple 로그인 노출 여부 — Android 앱에서만 숨긴다(효신님 결정 2026-09-04).
//   HIG는 비애플 플랫폼 제공도 허용하고 웹 OAuth라 기술적으로는 Android에서도 동작하지만,
//   Play 요건이 아니고 Android 사용자에게 낯선 선택지라 뺀다. iOS 앱·웹 SPA(app.sotda.com)는 노출.
//   ⚠ iOS에서 Apple로 가입한 사용자가 Android로 기기를 옮기면 이 화면에서 로그인 경로가 없다 — 문의 시 안내 필요.
const SHOW_APPLE = Capacitor.getPlatform() !== "android";

// 이메일 폼 입력 필드 공통 스타일 (cs-tokens — light/mocha 자동 대응)
const inputStyle: CSSProperties = {
  width: "100%",
  minHeight: 48,
  marginBottom: "var(--cs-s-3)",
  padding: "0 var(--cs-s-4)",
  background: "var(--cs-surface)",
  color: "var(--cs-ink)",
  border: "1px solid var(--cs-line)",
  borderRadius: "var(--cs-r-sm)",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

export function LoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<LoadingProvider>(null);
  const [error, setError] = useState<string | null>(null);

  // 이메일 폼 — 로그인 ↔ 가입 토글 (한 화면, 효신님 결정 2026-06-14)
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState("");
  // 최신 입력 미러 — 중복확인 늦은 응답의 stale 판정용 (terra 버그11)
  const nicknameRef = useRef("");
  // 닉네임 실시간 중복 확인 상태 (blur 시 profiles 조회)
  const [nicknameCheck, setNicknameCheck] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const [emailLoading, setEmailLoading] = useState(false);
  // Turnstile 캡차 토큰 — Supabase auth 호출의 options.captchaToken으로 넘긴다.
  // 토큰은 1회용이라 인증 실패 후 재시도하려면 위젯을 reset해 새 토큰을 받아야 한다.
  const [captchaToken, setCaptchaToken] = useState("");
  // 위젯 자체가 못 뜬 경우(호스트명 미등록·네트워크 차단 등) — 이때는 게이트를 열어준다.
  // 🔑 진짜 차단선은 **서버(Supabase Attack Protection)**이고 이 게이트는 UX용이다.
  //    fail-closed로 두면 위젯 로드 실패가 곧 "로그인 불가"가 된다(가용성 사고).
  //    서버가 켜져 있으면 토큰 없는 요청은 어차피 서버가 거부하고 사유도 정확히 돌려준다.
  const [captchaFailed, setCaptchaFailed] = useState(false);
  // 위젯 실패 코드 — 화면에 같이 띄운다. 사용자에겐 문의 시 전달할 값이고,
  // 우리에겐 "확인 실패" 한 줄로는 못 좁히던 원인(도메인/사이트키/네트워크)을 가르는 유일한 단서다.
  const [captchaErrCode, setCaptchaErrCode] = useState("");
  const turnstileRef = useRef<TurnstileInstance>(null);
  // 인증 진입점 단일 잠금 — 이메일·소셜 중 하나 진행 중이면 다른 시작 차단 (terra 버그12)
  const authBusy = isAuthBusy(emailLoading, loading);
  // 가입 완료 후 확인 메일 안내 (보낸 주소 보관)
  const [signupSentTo, setSignupSentTo] = useState<string | null>(null);

  // 로그인 성공 후 이동 — 첫 로그인(미온보딩)이면 온보딩으로 (소셜/이메일 공용)
  // 온보딩 플래그는 user.id 단위 (terra 버그10 — 같은 기기 신규 사용자 스킵 방지)
  const goAfterLogin = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    await flushPendingMbtiDiagnosis().catch((saveError) => {
      console.warn("[mbti/pending] 로그인 후 저장 실패", saveError);
    });
    navigate((await isOnboarded(session?.user?.id)) ? "/" : "/onboarding", { replace: true });
  }, [navigate]);

  // OAuth 콜백 도착 여부 — 시스템 브라우저가 닫혔을 때 취소인지 성공 처리 중인지 가르는 기준
  const callbackReceivedRef = useRef(false);
  const loadingRef = useRef<LoadingProvider>(null);
  loadingRef.current = loading;

  // 사용자가 OAuth 동의창(시스템 브라우저)을 직접 닫은 경우 — 예전엔 성공/실패 이벤트로만 loading을
  //   지워서 취소하면 "로그인 중…"이 영원히 남았다(8/22 메모 9). 취소는 실패가 아니라 대기 해제다.
  //   성공 경로에선 브라우저 닫힘 직전·직후에 콜백 URL이 먼저 도착하므로, 잠깐 기다렸다가
  //   콜백 신호가 없고 아직 대기 중일 때만 취소로 판정한다.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onCallback = () => { callbackReceivedRef.current = true; };
    window.addEventListener("sotda:auth:callback-received", onCallback);
    const handle = Browser.addListener("browserFinished", () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (callbackReceivedRef.current || !loadingRef.current) return; // 성공 처리 중이거나 이미 해제됨
        setLoading(null);
        setError("로그인을 취소했어요. 다시 시도해 주세요.");
      }, 800);
    });
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("sotda:auth:callback-received", onCallback);
      handle.then((h) => h.remove()).catch(() => {});
    };
  }, []);

  // deeplink.ts가 발행하는 이벤트 수신
  useEffect(() => {
    const onSuccess = async () => {
      callbackReceivedRef.current = false;
      setLoading(null);
      setError(null);
      // ④ 첫 로그인이면 온보딩으로 (deeplink와 동일 판정 — 충돌 없이 일치)
      await goAfterLogin();
    };
    const onError = (e: Event) => {
      callbackReceivedRef.current = false;
      setLoading(null);
      const detail = (e as CustomEvent).detail;
      setError(typeof detail === "string" ? detail : "로그인에 실패했어요.");
    };
    window.addEventListener("sotda:auth:login-success", onSuccess);
    window.addEventListener("sotda:auth:login-error", onError);
    return () => {
      window.removeEventListener("sotda:auth:login-success", onSuccess);
      window.removeEventListener("sotda:auth:login-error", onError);
    };
  }, [goAfterLogin]);

  async function onApple() {
    if (authBusy) return; // terra 버그12
    callbackReceivedRef.current = false; // 새 시도 — 이전 시도의 콜백 신호 잔여 제거
    setLoading("apple");
    setError(null);
    try {
      await startAppleOAuth();
    } catch (e) {
      setLoading(null);
      setError((e as Error).message);
    }
  }

  async function onGoogle() {
    if (authBusy) return; // 이메일/다른 OAuth 진행 중 — 이중 인증 흐름 차단 (terra 버그12)
    callbackReceivedRef.current = false; // 새 시도 — 이전 시도의 콜백 신호 잔여 제거
    setLoading("google");
    setError(null);
    try {
      await startGoogleOAuth();
    } catch (e) {
      setLoading(null);
      setError((e as Error).message);
    }
  }

  async function onNaver() {
    if (authBusy) return; // terra 버그12
    callbackReceivedRef.current = false; // 새 시도 — 이전 시도의 콜백 신호 잔여 제거
    setLoading("naver");
    setError(null);
    try {
      await startNaverOAuth();
    } catch (e) {
      setLoading(null);
      setError((e as Error).message);
    }
  }

  async function onKakao() {
    if (authBusy) return; // terra 버그12
    callbackReceivedRef.current = false; // 새 시도 — 이전 시도의 콜백 신호 잔여 제거
    setLoading("kakao");
    setError(null);
    try {
      await startKakaoOAuth();
    } catch (e) {
      setLoading(null);
      setError((e as Error).message);
    }
  }

  // 이메일 로그인/가입 — mode에 따라 분기
  async function onEmailSubmit(e: FormEvent) {
    e.preventDefault();
    if (authBusy) return; // OAuth 진행 중 Enter 제출 등 이중 진입 차단 (terra 버그12)
    setError(null);

    // 캡차 게이트 — 위젯이 **정상인데** 아직 토큰이 안 온 경우만 막는다(보안 확인 진행 중).
    // 위젯 미렌더(사이트키 없음·네이티브 앱)이거나 위젯이 실패했으면 통과시킨다.
    if (CAPTCHA_WIDGET_ENABLED && !captchaToken && !captchaFailed) {
      setError("잠시 후 다시 시도해주세요. (보안 확인 진행 중)");
      return;
    }

    try {
      if (mode === "signup") {
        // 클라 1차 검증 (이메일/비번/닉네임 순차)
        const v = validateSignupForm({ email, password, nickname });
        if (!v.valid) {
          setError(v.message ?? "입력 정보를 확인해주세요.");
          return;
        }
        setEmailLoading(true);
        // 닉네임 중복 사전 확인 (웹 signup 정합 — anon RLS로 profiles.nickname 조회)
        const { data: existing } = await supabase
          .from("profiles")
          .select("id")
          .eq("nickname", nickname)
          .maybeSingle();
        if (existing) {
          setError("이미 사용 중인 닉네임입니다.");
          setEmailLoading(false);
          return;
        }
        // 가입 — 확인 메일 발송 (mailer_autoconfirm=false라 항상 메일 인증 필요)
        const { data: signData, error: signErr } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: { nickname }, // raw_user_meta_data → DB 트리거가 profiles에 반영
            emailRedirectTo: Capacitor.isNativePlatform()
              ? `${EMAIL_REDIRECT_BASE}/auth/callback`
              : getWebOAuthRedirectUri(),
            captchaToken: captchaToken || undefined,
          },
        });
        setEmailLoading(false);
        if (signErr) {
          // 토큰은 1회용 — 실패했으면 새로 받아야 재시도가 된다
          turnstileRef.current?.reset();
          setCaptchaToken("");
          setError(translateAuthError(signErr, "signup"));
          return;
        }
        // 이미 가입된 이메일 — Supabase는 계정 노출 방지로 "성공한 척"하고 메일을 안 보냄
        // (소셜로 이미 가입된 이메일 포함). 이 경우 identities가 빈 배열 → 명확히 안내
        if (!signData.user?.identities?.length) {
          setError("이미 가입된 이메일이에요. 로그인하거나, 구글·네이버·카카오로 로그인해주세요.");
          return;
        }
        // 확인 메일 안내 + 로그인 모드로 전환 (메일 인증 후 로그인하도록)
        setSignupSentTo(email.trim());
        setMode("login");
        setPassword("");
        setNickname("");
        return;
      }

      // 로그인 — signInWithPassword (성공 시 onAuthStateChange가 세션 반영)
      setEmailLoading(true);
      const { error: loginErr } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
        options: { captchaToken: captchaToken || undefined },
      });
      setEmailLoading(false);
      if (loginErr) {
        // 토큰은 1회용 — 실패했으면 새로 받아야 재시도가 된다
        turnstileRef.current?.reset();
        setCaptchaToken("");
        // N-42: 로그인 500을 "확인 메일 발송 실패"로 안내하던 문제 — 문맥을 넘긴다.
        setError(translateAuthError(loginErr, "login"));
        return;
      }
      await goAfterLogin();
    } catch (err) {
      // 네트워크 단절 등 throw 대비 — 로딩 해제 + 한국어 안내
      setEmailLoading(false);
      setError(translateAuthError({ message: (err as Error)?.message }, mode === "signup" ? "signup" : "login"));
    }
  }

  // 로그인 ↔ 가입 전환 (입력 에러/안내 초기화)
  function toggleMode() {
    setMode((m) => (m === "login" ? "signup" : "login"));
    setError(null);
    setSignupSentTo(null);
    setNicknameCheck("idle");
  }

  // 닉네임 실시간 중복 확인 (입력 후 blur 시 profiles 조회 → 사용 가능/중복 표시)
  // 늦은 응답의 새 닉네임 오반영(stale) + error 시 '사용 가능' 오표시 방지 (terra 버그11)
  async function checkNickname() {
    const requested = nickname;
    const v = validateNickname(requested);
    if (!v.valid) {
      setNicknameCheck("idle"); // 형식 미달이면 표시 안 함 (가입 시 검증이 잡음)
      return;
    }
    setNicknameCheck("checking");
    try {
      const { data, error: qErr } = await supabase
        .from("profiles").select("id").eq("nickname", requested).maybeSingle();
      const next = resolveNicknameCheck(requested, nicknameRef.current, {
        data: (data as { id: string } | null) ?? null,
        error: qErr,
      });
      if (next) setNicknameCheck(next); // null = stale 응답 — 현재 입력의 상태 유지
    } catch {
      setNicknameCheck("idle");
    }
  }

  // 비밀번호 실시간 체크리스트 (가입 모드 UI)
  const pw = passwordStatus(password);

  // 소셜 진행 중 상태 문구 — 아이콘 버튼 4종은 글자가 없어 버튼 밖 한 줄로 알린다
  const socialStatus =
    loading === "apple" ? "Apple 인증 중…"
    : loading === "google" ? "구글 인증 중…"
    : loading === "naver" ? "네이버 인증 중…"
    : loading === "kakao" ? "카카오 인증 중…"
    : null;

  return (
    <TabLayout page="home">
      <div
        className="cs-root"
        style={{
          display: "flex",
          flexDirection: "column",
          background: "var(--cs-bg)",
        }}
      >
        {/* ── ① 브랜드 히어로 (D-1) ──
            로그인 벽 구조라 이 화면이 앱의 첫인상 전부다 → 상단 약 40%를 솟다 아이덴티티로 쓴다.
            표면 = 홈 "오늘의 솟다" 히어로와 같은 그라데(--cs-hero-ink → oklch 0.24) — 화면 3곳 히어로 단일 톤(design.md §H-2).
            카피는 App Store 정본에서 가져왔다(2026-08-22-appstore-listing-copy §1 부제 · §2 프로모션 텍스트) — 화면·스토어 문구 이원화 방지.
            캐릭터 = 황둥이(기본 화자·앞치마에 솟다 로고). 배경은 accent 계열 잉크 표면이라 §G-1 "캐릭터 배경 무채" 원칙과 충돌하지 않는다
            (홈 히어로도 잉크 위에 화자 캐릭터를 올린다). 이미지는 로컬 번들 자산이라 네트워크 비용 0. */}
        <section
          aria-label="솟다 소개"
          style={{
            position: "relative",
            overflow: "hidden",
            minHeight: "clamp(236px, 40vh, 380px)",
            padding: "var(--cs-s-6) var(--cs-s-6) var(--cs-s-5)",
            background: "linear-gradient(160deg, var(--cs-hero-ink), oklch(0.24 0.045 158))",
            color: "var(--cs-on-ink)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {/* 워드마크 — 벡터 로고가 없어 앱 아이콘(다크그린 바탕·새싹)을 그대로 쓴다. 히어로와 같은 색상군이라 경계 없이 녹는다 */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative", zIndex: 1 }}>
            <img
              src="/icon-192.png"
              alt=""
              width={36}
              height={36}
              style={{ borderRadius: 9, boxShadow: "0 0 0 1px rgba(241,236,224,.14)", objectFit: "cover" }}
            />
            <span
              style={{
                fontFamily: "var(--cs-font-serif)",
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "var(--cs-on-ink)",
              }}
            >
              솟다
            </span>
          </div>

          {/* 가치 한 줄 — 캐릭터가 우측을 차지하므로 카피 폭을 62%로 잠근다(SE3 375px에서 헤드라인 2줄·서브 2줄이 되는 폭) */}
          <div style={{ position: "relative", zIndex: 1, maxWidth: "62%", marginTop: "var(--cs-s-6)" }}>
            <p
              style={{
                fontSize: "var(--cs-t-xs)",
                fontWeight: 700,
                letterSpacing: "0.04em",
                color: "var(--cs-on-ink-dim)",
                marginBottom: "var(--cs-s-2)",
              }}
            >
              기록하고 복기하는 투자 연습
            </p>
            <h1
              style={{
                fontFamily: "var(--cs-font-serif)",
                fontSize: 21,
                fontWeight: 600,
                lineHeight: 1.35,
                letterSpacing: "-0.02em",
                color: "var(--cs-on-ink)",
                margin: "0 0 var(--cs-s-3)",
              }}
            >
              매수 전과 후,
              <br />
              내 판단을 남겨두세요.
            </h1>
            <p style={{ fontSize: "var(--cs-t-sm)", lineHeight: 1.55, color: "var(--cs-on-ink-dim)" }}>
              가상 자금으로 연습하고, AI가 반복된 패턴을 짚어줍니다.
            </p>
          </div>

          {/* 캐릭터 — 우측 하단 고정, 히어로 높이의 88%를 차지하고 아래로 살짝 잘려 "서 있는" 인상 */}
          <img
            src="/redesign-assets/lcard/hwangdoongi.png"
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              right: -6,
              bottom: -14,
              height: "88%",
              width: "auto",
              objectFit: "contain",
              pointerEvents: "none",
              filter: "drop-shadow(0 10px 18px rgba(0,0,0,.28))",
            }}
          />
        </section>

        {/* ── ② 로그인 영역 ── */}
        <div style={{ padding: "var(--cs-s-6) var(--cs-s-6) var(--cs-s-4)" }}>
          {/* 서브카피 (D-3 정정) — 맨 위 버튼이 Apple인데 설명에 없던 것을 바로잡았다 */}
          <p
            style={{
              fontSize: "var(--cs-t-sm)",
              color: "var(--cs-mute)",
              margin: "0 0 var(--cs-s-4)",
              lineHeight: 1.6,
              textAlign: "center",
            }}
          >
            {SHOW_APPLE ? "Apple·구글·네이버·카카오" : "구글·네이버·카카오"} 또는 이메일로 시작하세요.
          </p>

          {error && (
            <div
              role="alert"
              style={{
                marginBottom: "var(--cs-s-4)",
                padding: "var(--cs-s-3) var(--cs-s-4)",
                background: "var(--cs-rise-soft)",
                color: "var(--cs-rise)",
                border: "1px solid var(--cs-rise)",
                borderRadius: "var(--cs-r-sm)",
                fontSize: "var(--cs-t-sm)",
                lineHeight: 1.5,
                fontWeight: 600,
              }}
            >
              ⚠ {error}
            </div>
          )}

          {/* 소셜 4종 — 52×52 아이콘 버튼 한 줄로 통일 (효신님 결정 2026-09-04 · design doc §3-6).
              브랜드 색은 아이콘 버튼 안에서만 쓴다(효신님 결정 2026-05-26 유지).
              형태 = 52×52 · radius 12(--cs-r-md): 카카오 가이드가 컨테이너 radius를 12px로 고정하고 원형을 규정하지 않아
              "원형" 대신 라운드 사각을 택했다(design doc §3-3). Google은 "G 단독은 버튼 경계가 있을 때만" 허용 → 테두리 유지.
              Apple = HIG "logo-only" 버튼(1:1 · 검정 바탕 + 흰 로고 · 둥근사각 마스크 허용). 맨 앞에 두고 다른 3종과 같은 크기라
              HIG "다른 로그인 버튼보다 작지 않게 + 스크롤 없이 보이게"를 만족한다. Android 앱에서는 SHOW_APPLE=false로 숨긴다. */}
          <div style={{ display: "flex", justifyContent: "center", gap: "var(--cs-s-4)" }}>
            {SHOW_APPLE && (
              <SocialIconButton
                label="Apple로 계속하기"
                onClick={onApple}
                disabled={authBusy}
                dimmed={!!loading && loading !== "apple"}
                busy={loading === "apple"}
                style={{ background: "#000", color: "#fff" }}
              >
                <AppleIcon />
              </SocialIconButton>
            )}
            <SocialIconButton
              label="Google로 시작"
              onClick={onGoogle}
              disabled={authBusy}
              dimmed={!!loading && loading !== "google"}
              busy={loading === "google"}
              style={{ background: "#fff", color: "#1F1F1F", border: "1px solid #DADCE0" }}
            >
              <GoogleIcon />
            </SocialIconButton>
            <SocialIconButton
              label="네이버로 시작"
              onClick={onNaver}
              disabled={authBusy}
              dimmed={!!loading && loading !== "naver"}
              busy={loading === "naver"}
              style={{ background: "#03C75A", color: "#fff" }}
            >
              <NaverIcon />
            </SocialIconButton>
            <SocialIconButton
              label="카카오로 시작"
              onClick={onKakao}
              disabled={authBusy}
              dimmed={!!loading && loading !== "kakao"}
              busy={loading === "kakao"}
              style={{ background: "#FEE500", color: "#000" }}
            >
              <KakaoIcon />
            </SocialIconButton>
          </div>
          {/* 진행 상태 — 아이콘 버튼은 글자가 없으니 여기서 알린다 (스크린리더 polite) */}
          <p
            aria-live="polite"
            style={{
              minHeight: 18,
              margin: "var(--cs-s-2) 0 0",
              fontSize: "var(--cs-t-xs)",
              color: "var(--cs-mute)",
              textAlign: "center",
            }}
          >
            {socialStatus ?? ""}
          </p>

          {/* 이메일 폼 — 기본 펼침(효신님 결정 2026-09-04). 9/2의 "텍스트 링크 → 누르면 펼침"은 폐기:
              펼침 한 번이 마찰이고, 소셜이 위에 있어 폼이 있어도 Apple fold 요건은 유지된다. 화면은 스크롤을 수용한다. */}
          {(
            <div>
              {/* ── 구분선 ── */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  margin: "var(--cs-s-5) 0 var(--cs-s-4)",
                }}
              >
                <span style={{ flex: 1, height: 1, background: "var(--cs-line)" }} />
                <span style={{ fontSize: "var(--cs-t-xs)", color: "var(--cs-mute)" }}>
                  {mode === "login" ? "이메일로 로그인" : "이메일로 가입"}
                </span>
                <span style={{ flex: 1, height: 1, background: "var(--cs-line)" }} />
              </div>

              {/* 가입 완료 → 확인 메일 안내 */}
              {signupSentTo && (
                <div
                  role="status"
                  style={{
                    marginBottom: "var(--cs-s-4)",
                    padding: "var(--cs-s-3) var(--cs-s-4)",
                    background: "var(--cs-surface-2)",
                    color: "var(--cs-ink)",
                    border: "1px solid var(--cs-line)",
                    borderRadius: "var(--cs-r-sm)",
                    fontSize: "var(--cs-t-sm)",
                    lineHeight: 1.6,
                  }}
                >
                  ✉️ <strong style={{ color: "var(--cs-accent)" }}>{signupSentTo}</strong>로 인증 메일을 보냈어요.
                  <br />
                  메일의 링크를 누르면 가입이 완료돼요 — 이후 이메일로 로그인해 주세요.
                </div>
              )}

              <form onSubmit={onEmailSubmit}>
                {mode === "signup" && (
                  <>
                    <input
                      type="text"
                      name="nickname"
                      autoComplete="username"
                      value={nickname}
                      onChange={(e) => {
                        setNickname(e.target.value);
                        nicknameRef.current = e.target.value; // stale 판정 기준 (terra 버그11)
                        setNicknameCheck("idle"); // 재입력 시 이전 결과 초기화 (다시 blur로 확인)
                      }}
                      onBlur={checkNickname}
                      placeholder="닉네임 (2~20자)"
                      disabled={authBusy}
                      style={inputStyle}
                    />
                    {nicknameCheck !== "idle" && (
                      <div
                        style={{
                          fontSize: "var(--cs-t-xs)",
                          margin: "-6px 2px 10px",
                          color:
                            nicknameCheck === "taken"
                              ? "var(--cs-rise)"
                              : nicknameCheck === "available"
                                ? "var(--cs-accent)"
                                : "var(--cs-mute)",
                        }}
                      >
                        {nicknameCheck === "checking"
                          ? "확인 중…"
                          : nicknameCheck === "available"
                            ? "✓ 사용 가능한 닉네임이에요"
                            : "이미 사용 중인 닉네임이에요"}
                      </div>
                    )}
                  </>
                )}
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일"
                  required
                  disabled={authBusy}
                  style={inputStyle}
                />
                <input
                  type="password"
                  name="password"
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호"
                  required
                  disabled={authBusy}
                  style={inputStyle}
                />

                {/* 가입 모드 — 비밀번호 실시간 체크리스트 */}
                {mode === "signup" && password.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px 12px",
                      margin: "2px 2px var(--cs-s-3)",
                    }}
                  >
                    <PwCheck ok={pw.length} label="8자 이상" />
                    <PwCheck ok={pw.letter} label="영문" />
                    <PwCheck ok={pw.number} label="숫자" />
                    <PwCheck ok={pw.special} label="특수문자" />
                  </div>
                )}

                {/* Turnstile CAPTCHA — 사이트키가 있고 **네이티브가 아닐 때만** 렌더
                    (WKWebView 미지원 — 상단 CAPTCHA_WIDGET_ENABLED 주석 참조).
                    theme는 앱이 light/mocha를 오가므로 auto로 둔다(웹은 dark 고정). */}
                {CAPTCHA_WIDGET_ENABLED && (
                  <div style={{ display: "flex", justifyContent: "center", margin: "0 0 var(--cs-s-3)" }}>
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => { setCaptchaToken(token); setCaptchaFailed(false); setCaptchaErrCode(""); }}
                      // ⚠ 에러 코드를 버리지 말 것 — 2026-08-03부터 "확인 실패"만 보이고 원인을 못 좁힌 이유가
                      //   여기서 code를 통째로 버린 것이었다. Turnstile은 실패 사유를 코드로 준다
                      //   (110200=도메인 미등록 / 300xxx·600xxx=위젯 내부 / 106xxx=사이트키 문제).
                      onError={(code) => { setCaptchaToken(""); setCaptchaFailed(true); setCaptchaErrCode(String(code || "")); console.warn("[turnstile] 위젯 실패", { code, origin: window.location.origin }); }}
                      onExpire={() => setCaptchaToken("")}
                      options={{ theme: "auto", size: "flexible" }}
                    />
                  </div>
                )}
                {captchaFailed && (
                  <div style={{ fontSize: 12, color: "var(--cs-mute)", textAlign: "center", margin: "0 0 var(--cs-s-3)" }}>
                    보안 확인에 실패했어요. 로그인은 그대로 진행됩니다.
                    {captchaErrCode && <span style={{ fontFamily: "var(--cs-font-mono)" }}> (코드 {captchaErrCode})</span>}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={authBusy}
                  style={{
                    width: "100%",
                    minHeight: 48,
                    padding: "0 var(--cs-s-4)",
                    background: "var(--cs-accent)",
                    color: "var(--cs-accent-ink)",
                    border: "none",
                    borderRadius: "var(--cs-r-md)",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: emailLoading ? "wait" : "pointer",
                    opacity: emailLoading ? 0.6 : 1,
                    transition: "opacity 0.15s",
                  }}
                >
                  {emailLoading
                    ? mode === "login"
                      ? "로그인 중…"
                      : "가입 중…"
                    : mode === "login"
                      ? "이메일로 로그인"
                      : "이메일로 가입"}
                </button>
              </form>

              {/* 비밀번호 재설정 — 로그인 모드에서만 (D-5). 이메일 폼 바로 아래에 둔다(폼이 문맥).
                  앱에 재설정 UI가 없어 웹 /auth/forgot-password를 시스템 브라우저로 연다.
                  상대경로 href는 앱 번들을 가리키므로 절대 URL + openExternal (externalLink.ts 주석 참고). */}
              {mode === "login" && (
                <p style={{ marginTop: "var(--cs-s-3)", marginBottom: 0, textAlign: "center" }}>
                  <a
                    href={FORGOT_PASSWORD_URL}
                    onClick={(e) => { e.preventDefault(); openExternal(FORGOT_PASSWORD_URL); }}
                    style={{
                      fontSize: "var(--cs-t-sm)",
                      color: "var(--cs-mute)",
                      textDecoration: "underline",
                    }}
                  >
                    비밀번호를 잊으셨나요?
                  </a>
                </p>
              )}

              {/* 로그인 ↔ 가입 토글 */}
              <p
                style={{
                  marginTop: "var(--cs-s-3)",
                  marginBottom: 0,
                  fontSize: "var(--cs-t-sm)",
                  color: "var(--cs-mute)",
                  textAlign: "center",
                }}
              >
                {mode === "login" ? "계정이 없으신가요? " : "이미 계정이 있으신가요? "}
                <button
                  type="button"
                  onClick={toggleMode}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "var(--cs-accent)",
                    fontWeight: 700,
                    fontSize: "var(--cs-t-sm)",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  {mode === "login" ? "회원가입" : "로그인"}
                </button>
              </p>
            </div>
          )}

          {/* 이용약관·개인정보처리방침 — 항상 최하단. 앱은 로컬 번들에서 뜨므로 "/privacy" 상대경로는 웹 문서로 나가지 못한다.
              절대 URL + 시스템 브라우저로 연다 (lib/externalLink.ts 주석 참고)
              Apple 1.2(UGC): 가입·로그인 *전에* 이용약관(EULA)이 제시돼야 한다 — 개인정보처리방침만으로는 부족(2026-09-02 3차 반려 · 9/4 main 합류 시 이식). */}
          <p
            style={{
              marginTop: "var(--cs-s-6)",
              fontSize: "var(--cs-t-xs)",
              color: "var(--cs-mute)",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            계속하면{" "}
            <a
              href={TERMS_URL}
              onClick={(e) => { e.preventDefault(); openExternal(TERMS_URL); }}
              style={{
                color: "var(--cs-accent)",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              이용약관
            </a>
            {" 및 "}
            <a
              href={PRIVACY_URL}
              onClick={(e) => { e.preventDefault(); openExternal(PRIVACY_URL); }}
              style={{
                color: "var(--cs-accent)",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              개인정보처리방침
            </a>
            에<br />
            동의한 것으로 간주됩니다.
          </p>
        </div>
      </div>
    </TabLayout>
  );
}

// 소셜 아이콘 버튼 (Apple·Google·네이버·카카오 공용) — 52×52·radius 12. 글자가 없으므로 aria-label 필수.
// 진행 중(busy)이면 아이콘을 살짝 눌러 보이게 하고, 다른 provider 진행 중(dimmed)이면 반투명.
function SocialIconButton({
  label, onClick, disabled, dimmed, busy, style, children,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  dimmed: boolean;
  busy: boolean;
  style: CSSProperties;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 52,
        height: 52,
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        borderRadius: "var(--cs-r-md)",
        cursor: disabled ? "wait" : "pointer",
        opacity: dimmed ? 0.5 : 1,
        transform: busy ? "scale(0.94)" : "none",
        transition: "opacity 0.15s, transform 0.15s",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// Apple 로고 — HIG 커스텀 버튼 규정: 로고·글자는 검정/흰만, currentColor라 검정 바탕에서 흰색으로 따라간다.
//   로고 전용 버튼은 1:1 비율이 규정이고 아트워크 패딩만큼 여백을 둔다(24px 로고 / 52px 버튼).
function AppleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 814 1000" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zM554 159.4c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"
      />
    </svg>
  );
}

// Google "G" — 브랜드 표준 4색 고정 (버튼 경계 안에서만 단독 사용 허용)
function GoogleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.5-5.9 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C34.9 6.1 29.7 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7C34.9 6.1 29.7 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.6 0 10.7-2.1 14.5-5.6l-6.7-5.7C29.7 34.4 27 35 24 35c-5.4 0-9.6-3.5-11.3-8l-6.6 5.1C9.6 39.6 16.3 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.7 5.7c-.5.4 7.2-5.2 7.2-15.4 0-1.3-.1-2.6-.4-3.9z" />
    </svg>
  );
}

// 네이버 N — 버튼 글자색(흰) 상속. 컨테이너 #03C75A(네이버 그린)
function NaverIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M16.273 12.845L7.376 0H0v24h7.726V11.156L16.624 24H24V0h-7.727z" />
    </svg>
  );
}

// 카카오 말풍선 심볼 — 가이드 규정 #000. 컨테이너 #FEE500
function KakaoIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M12 3C6.477 3 2 6.463 2 10.735c0 2.764 1.874 5.19 4.69 6.56-.207.77-.748 2.79-.856 3.222-.134.534.196.527.412.384.17-.113 2.703-1.836 3.802-2.585.63.093 1.281.142 1.95.142 5.523 0 10-3.463 10-7.735C22 6.463 17.523 3 12 3z" />
    </svg>
  );
}

// 비밀번호 정책 충족 여부 1줄 표시 (가입 모드 체크리스트)
function PwCheck({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      style={{
        fontSize: "var(--cs-t-xs)",
        color: ok ? "var(--cs-accent)" : "var(--cs-mute)",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <span>{ok ? "✓" : "○"}</span>
      <span>{label}</span>
    </span>
  );
}
